/// <amd-module name="VSSUI/Components/VssPersona/VssPersona" />

import * as React from "react";
import { Icon } from "VSSUI/Icon";
import { Tooltip } from "VSSUI/TooltipEx";
import { css, KeyCode } from "VSSUI/Util";
import { getColorString, IColor } from "VSSUI/Utilities/Color";
import { getInitialsColorFromName, getInitialsFromName } from "./VssPersona.Initials";
import { IVssPersonaProps, VssPersonaSize } from "./VssPersona.Props";

export interface IVssPersonaState {
    imageError: boolean;
    imageUrlVal: string | undefined;
    showPersonaCard: boolean;
}

/**
 * Renders a user's profile/identity/avatar image.
 */
export class VssPersona extends React.Component<IVssPersonaProps, IVssPersonaState> {
    private targetElement: HTMLElement;

    constructor(props: IVssPersonaProps) {
        super(props);

        this.state = {
            imageError: false,
            showPersonaCard: false,
            imageUrlVal: this._getImageUrl(props)
        };
    }

    public UNSAFE_componentWillReceiveProps(nextProps: IVssPersonaProps): void {
        this.setState({
            showPersonaCard: false,
            imageUrlVal: this._getImageUrl(nextProps)
        });
    }

    public shouldComponentUpdate(nextProps: IVssPersonaProps, nextState: IVssPersonaState): boolean {
        if (!nextProps.identityDetailsProvider && (this.props.imageUrl !== nextProps.imageUrl || this.props.displayName !== nextProps.displayName)) {
            return true;
        } else if (!this.props.identityDetailsProvider && !nextProps.identityDetailsProvider) {
            return false;
        } else if (!this.props.identityDetailsProvider || !nextProps.identityDetailsProvider) {
            return true;
        }
        return (
            this.props.size !== nextProps.size ||
            this.props.cssClass !== nextProps.cssClass ||
            this.props.identityDetailsProvider !== nextProps.identityDetailsProvider ||
            this.state.showPersonaCard !== nextState.showPersonaCard
        );
    }

    public render(): JSX.Element {
        const { ariaLabel, identityDetailsProvider, size = "medium", imgAltText = "", imageUrl, displayName } = this.props;
        const imageUrlVal = !identityDetailsProvider ? imageUrl : this.state.imageUrlVal;

        // Set the focus and aria-expand attributes based on props passed
        const additionalAttributes: { [key: string]: any } = {};
        additionalAttributes["role"] = "img";
        if (this.props.dataIsFocusable) {
            additionalAttributes["data-is-focusable"] = true;
        }
        if (this.props.isTabStop) {
            additionalAttributes["tabIndex"] = 0;
        }

        // Setting the aria related properties and user action delegates unless we supress persona card or persona card isnt't provided
        if (!this.props.suppressPersonaCard && this.props.identityDetailsProvider && this.props.identityDetailsProvider.onRenderPersonaCard) {
            additionalAttributes["aria-expanded"] = this.state.showPersonaCard;
            additionalAttributes["onKeyDown"] = this.handleKeyDown;
            additionalAttributes["onClick"] = this.showPersonaCard;
            additionalAttributes["role"] = "button";
        }

        const displayNameVal = !identityDetailsProvider ? displayName : identityDetailsProvider.getDisplayName();

        if (ariaLabel) {
            additionalAttributes["aria-label"] = ariaLabel;
        } else if (displayNameVal) {
            additionalAttributes["aria-label"] = displayNameVal;
        } else {
            additionalAttributes["aria-hidden"] = "true";
        }

        const backgroundColor: IColor | undefined = displayNameVal === undefined ? undefined : getInitialsColorFromName(displayNameVal);

        const imageElement: JSX.Element =
            imageUrlVal !== undefined && !this.state.imageError ? (
                <img className={"vss-Persona-content using-image"} src={imageUrlVal} alt={imgAltText} onError={this.onImageError} />
            ) : (
                <div className={css("vss-Persona-content", size)} style={backgroundColor && { background: getColorString(backgroundColor) }}>
                    {displayNameVal ? <span>{getInitialsFromName(displayNameVal)}</span> : <Icon iconName="Contact" />}
                </div>
            );

        // Getting the reference to the div around the image because the Callout within PersonaCard has positioning problems in some cases when passing in img element as the target
        return (
            <>
                <Tooltip text={displayNameVal} showOnFocus={true}>
                    <div
                        className={css("vss-Persona flex-noshrink", this.props.className, this.props.cssClass, size)}
                        ref={this.setTargetElement}
                        {...additionalAttributes}
                    >
                        {imageElement}
                    </div>
                </Tooltip>
                {!this.props.suppressPersonaCard &&
                    this.state.showPersonaCard &&
                    identityDetailsProvider &&
                    identityDetailsProvider.onRenderPersonaCard &&
                    identityDetailsProvider.onRenderPersonaCard(this.targetElement, this.hidePersonaCard)}
            </>
        );
    }

    private setTargetElement = (element: HTMLDivElement): void => {
        this.targetElement = element;
    };

    private onImageError = (event: React.SyntheticEvent<HTMLImageElement, Event>): void => {
        if (this.props.showInitialsOnImageError) {
            this.setState({ imageError: true });
        } else if (this.props.onImageError) {
            this.props.onImageError(event);
        }
    };

    private handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
        if (e.keyCode === KeyCode.enter || e.keyCode === KeyCode.space) {
            this.showPersonaCard();
        }
    };

    private showPersonaCard = (): void => {
        if (!this.props.suppressPersonaCard) {
            this.setState({ showPersonaCard: true });
        }
    };

    private hidePersonaCard = (): void => {
        this.setState({ showPersonaCard: false });
    };

    /**
     * Resolve the URL for the profile image.
     * @param props
     */
    private _getImageUrl(props: IVssPersonaProps): string | undefined {
        const { identityDetailsProvider, size = "medium" } = props;
        const sizePx = this._getSize(size);

        return identityDetailsProvider && identityDetailsProvider.getIdentityImageUrl(sizePx);
    }

    /**
     * Get the size in pixels for the given css class.
     * @param size
     */
    private _getSize(size: VssPersonaSize): number {
        switch (size) {
            case "extra-extra-small":
                return 16;
            case "extra-small":
                return 18;
            case "extra-small-plus":
                return 20;
            case "small":
                return 24;
            case "small-plus":
                return 28;
            case "medium":
                return 32;
            case "medium-plus":
                return 40;
            default:
            case "large":
                return 48;
            case "extra-large":
                return 72;
            case "extra-extra-large":
                return 100;
        }
    }
}
